import React, { useCallback, useMemo } from 'react';
import startOfToday from 'date-fns/startOfToday';

import { useTranslation } from '@lyearn/i18n';
import { Box } from '@lyearn/ui';
import DateFilterComp from '@/components/DateFilter';
import { DateFilterListItem, DateFilterListItemKey } from '@/components/DateFilter/constants';
import { DateFilterPosition } from '@/components/DateFilter/types';
import { FilterOperators } from '@/components/Filter/types';
import { DateFilter, UserType } from '@/types/schema';
import { getCurrentTimeZoneValue } from '@/utils/timezone';

import { ActionButton } from '../components/ActionButton';
import { DATE_FILTER_OPTIONS_VALUE, getDateFilterLabel, getSelectedValues } from './utils';

function DateRangeFilter({
  isOpen,
  selectedValue,
  onChange,
  defaultPlaceholder,
  options,
  selectedOperator,
  onToggleFilter,
  children,
  position = DateFilterPosition.Left,
  timezone: timezoneProp,
}: {
  selectedValue?: DateFilter;
  onChange: (value?: DateFilter) => void;
  defaultPlaceholder: string;
  options?: DateFilterListItem[];
  operators?: FilterOperators[];
  selectedOperator?: FilterOperators;
  isOpen?: boolean;
  onToggleFilter?: () => void;
  position?: DateFilterPosition;
  children?: React.ReactNode;
  timezone?: UserType['timezone'];
}) {
  const { t } = useTranslation();
  const timezone = timezoneProp ?? getCurrentTimeZoneValue();

  const onDone = useCallback(
    (startTime?: Date, endTime?: Date, selectedItem?: DateFilterListItem) => {
      if (!startTime || !endTime || !selectedItem) {
        onChange();

        return;
      }
      if (selectedItem.key === DateFilterListItemKey.Custom) {
        onChange({
          absolute: {
            startTime: startTime.toISOString(),
            endTime: endTime.toISOString(),
          },
          timezone,
        });
      } else if (selectedItem.key === DateFilterListItemKey.Overdue) {
        onChange({
          absolute: {
            endTime: startOfToday().toISOString(),
          },
          timezone,
        });
      } else {
        const value = DATE_FILTER_OPTIONS_VALUE[selectedItem.key];
        onChange({
          relative: value.relative,
          timezone,
        });
      }
    },
    [onChange, timezone],
  );

  const selectedValues = useMemo(
    () => getSelectedValues(selectedValue, options),
    [selectedValue, options],
  );

  return (
    <DateFilterComp
      childRenderer={(open, toggle, calendarButtonRef) =>
        children ?? (
          <ActionButton ref={calendarButtonRef} id="filter-date" isOpen={open} onClick={toggle}>
            <Box className="flex items-center">
              {getDateFilterLabel({
                t,
                defaultLabel: defaultPlaceholder,
                selectedValue,
                selectedOperator,
                options,
              })}
            </Box>
          </ActionButton>
        )
      }
      dateRange={selectedValues.dateRange}
      isOpen={isOpen}
      options={options}
      position={position}
      selectedDateFilterItem={selectedValues.selectedOption}
      onDone={onDone}
      onToggleFilter={onToggleFilter}
    />
  );
}

export default DateRangeFilter;
